import { MessagingPlatform } from './messagingPlatform.enum';

export const CALENDAR_PLATFORMS = {
  GOOGLE: 'google',
  OUTLOOK: 'outlook',
} as const;

export type CalendarPlatform =
  (typeof CALENDAR_PLATFORMS)[keyof typeof CALENDAR_PLATFORMS];

const CALENDAR_PLATFORM_VALUES: ReadonlySet<string> = new Set(
  Object.values(CALENDAR_PLATFORMS),
);

// Strict on purpose: an unrecognised value reaches the caller's own refusal branch rather
// than quietly resolving to Google.
export const toCalendarPlatform = (
  value: string | undefined | null,
): CalendarPlatform | undefined =>
  value && CALENDAR_PLATFORM_VALUES.has(value)
    ? (value as CalendarPlatform)
    : undefined;

// An axis CONVERSION, and the reason toCalendarPlatform can afford to be strict: calendar
// action steps are driven by the triggering message's data platform, so something has to say
// that a Gmail message implies the Google calendar. Anything else implies no calendar.
const CALENDAR_PLATFORM_BY_MESSAGE_PLATFORM: ReadonlyMap<
  string,
  CalendarPlatform
> = new Map([
  [MessagingPlatform.GOOGLE, CALENDAR_PLATFORMS.GOOGLE],
  [MessagingPlatform.GMAIL, CALENDAR_PLATFORMS.GOOGLE],
  [MessagingPlatform.GOOGLE_CALENDAR, CALENDAR_PLATFORMS.GOOGLE],
  [MessagingPlatform.MICROSOFT, CALENDAR_PLATFORMS.OUTLOOK],
  [MessagingPlatform.OUTLOOK, CALENDAR_PLATFORMS.OUTLOOK],
  [MessagingPlatform.OUTLOOK_CALENDAR, CALENDAR_PLATFORMS.OUTLOOK],
]);

export const calendarPlatformForMessage = (
  platform: string | undefined | null,
): CalendarPlatform | undefined =>
  platform ? CALENDAR_PLATFORM_BY_MESSAGE_PLATFORM.get(platform) : undefined;

// For a value read back from storage, which predates this axis and may still hold the
// messaging platform the editor used to persist. A tool argument is NOT this — it is supplied
// fresh and goes through toCalendarPlatform, so a wrong one is refused rather than guessed at.
export const toStoredCalendarPlatform = (
  value: string | undefined | null,
): CalendarPlatform | undefined =>
  toCalendarPlatform(value) ?? calendarPlatformForMessage(value);

export enum CalendarResponseStatus {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
  NEEDS_ACTION = 'needsAction',
}

export enum CalendarInviteAction {
  ACCEPT = 'ACCEPT',
  DECLINE = 'DECLINE',
  TENTATIVE = 'TENTATIVE',
  DELETE = 'DELETE',
  PROPOSE_NEW_TIME = 'PROPOSE_NEW_TIME',
}

export interface CalendarOrganizer {
  name?: string;
  email?: string;
}

export interface CalendarEventTime {
  startTime?: number;
  endTime?: number;
  timeZone?: string;
}

export interface CalendarEventAttendee {
  email: string;
  name?: string;
  responseStatus: CalendarResponseStatus;
}

export interface CalendarEventConferenceEntryPoint {
  uri?: string;
  label?: string;
}

export interface CalendarEventConferenceData {
  entryPoint?: CalendarEventConferenceEntryPoint;
}

export interface CalendarEventData {
  eventId?: string;
  icalUid?: string;
  responseStatus: CalendarResponseStatus;
  summary?: string;
  organizer?: CalendarOrganizer;
  eventTime?: CalendarEventTime;
  description?: string;
  attendees?: CalendarEventAttendee[];
  conferenceData?: CalendarEventConferenceData;
  recurringEventId?: string;
  sequence?: number;
}
